import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Button, Link, Alert } from '@one-thd/sui-atomic-components';
import { createBrowserHistory } from 'history';
import classNames from 'classnames';
import BalanceCard from './BalanceCard';
import { useAlert } from '../../hooks/useAlert';
import {
  URL,
  VERBIAGE,
  NUCLEUS_CHECKOUT_PATH,
  containerWidthClass,
  NEW_RELIC_ACTIONS
} from '../../utils/consts';
import { getAddToCartPayload, isLocalHost, checkIfBonusGiftCard } from '../../utils/utils';
import SelectAmount from './SelectAmount';
import { setAnalyticsData, setATCAnalytics } from '../../utils/Analytics';
import { useCart } from '../../hooks/useCart';
import { publishNewRelicPageAction } from '../../utils/new-relic';

const ReloadForm = ({
  cardNumber,
  cardLast4,
  cardError,
  reloadableAmount,
  availableAmount,
  isDrawer,
  savedPaymentId,
  onCancel,
  isReloadDisabled = false
}) => {
  const [showAmount, setShowAmount] = useState(!!savedPaymentId);
  const [reloadError, setReloadError] = useState(cardError);
  const isDisabled = !!reloadError || isReloadDisabled;
  const [addToCart, atcResponse] = useCart();
  const [showFaq, setShowFaq] = useState(false);
  const {
    loading: isAtcLoading,
    error: atcError,
    called: isAtcCalled
  } = atcResponse;
  const disableBtn = isAtcLoading && !atcError;
  const isGraphQlLoading = isAtcLoading && isAtcCalled;
  const { clearAlert } = useAlert();

  useEffect(() => {
    setAnalyticsData();
  }, []);

  useEffect(() => {
    if (cardNumber && checkIfBonusGiftCard(cardNumber)) {
      setShowFaq(true);
    }
  }, [cardNumber]);

  const navigateToCheckout = () => {
    const history = createBrowserHistory();
    history.push(NUCLEUS_CHECKOUT_PATH);
    history.go();
  };

  const handleAmountChange = () => {
    if (isDisabled) {
      setReloadError(null);
    }
  };

  const handleNextStep = (cartId) => {
    const reloadCheckout = {
      cartId,
      experience: 'giftcards-reload',
      previousPath: window.location.pathname
    };
    window.sessionStorage.setItem(
      'reloadCheckout',
      JSON.stringify(reloadCheckout)
    );
    navigateToCheckout();
  };

  const handleAddToCart = async (event, amount) => {
    publishNewRelicPageAction({
      actionName: NEW_RELIC_ACTIONS.SUBMIT_RELOAD_BUTTON_CLICKED,
      actionObject: { sectionName: 'reloadSection' }
    });
    if (isLocalHost() && typeof window !== 'undefined') {
      // eslint-disable-next-line no-alert
      window.alert('Reload Successful');
    } else {
      event.preventDefault();
      const cartRequestData = getAddToCartPayload(
        amount,
        availableAmount,
        cardNumber,
        savedPaymentId,
        cardLast4
      );
      try {
        const response = await addToCart({
          cartRequest: cartRequestData,
          requestContext: {
            experience: {
              type: 'GC_R'
            }
          }
        });
        const { cartId } = response.addToCart;
        publishNewRelicPageAction({
          actionName: NEW_RELIC_ACTIONS.SUBMIT_RELOAD_BUTTON_SUCCESS,
          actionObject: {
            sectionName: 'reloadSection',
            message: 'Reload amount ATC success'
          }
        });
        setATCAnalytics(cartId, amount);
        if (cartId) {
          handleNextStep(cartId);
        }
      } catch (error) {
        setReloadError(VERBIAGE.technicalError);
        publishNewRelicPageAction({
          actionName: NEW_RELIC_ACTIONS.SUBMIT_RELOAD_BUTTON_FAIL,
          actionObject: {
            sectionName: 'reloadSection',
            message: 'Reload amount ATC fail - exceptions'
          }
        });
      }
    }
  };

  const handleShowReload = () => {
    setShowAmount(true);
    publishNewRelicPageAction({
      actionName: NEW_RELIC_ACTIONS.RELOAD_BUTTON_CLICKED,
      actionObject: {
        sectionName: 'reloadSection',
        message: 'Reload button clicked after balance check'
      }
    });
  };

  const handleCheckAnotherBalance = () => {
    onCancel();
    clearAlert();
  };

  const wrapperClassName = classNames(
    'sui-flex sui-flex-col sui-items-center sui-justify-between',
    {
      'sui-h-full': isDrawer
    }
  );

  const buttonWrapperClass = 'sui-mt-3 sui-w-full';

  return (
    <div className={wrapperClassName} data-testid="reload-form-wrapper">
      <div className={`sui-max-w-full ${containerWidthClass}`}>
        <BalanceCard
          cardNumber={cardNumber || cardLast4}
          availableAmount={availableAmount}
        />
        {cardError && (
          <div className="sui-mt-4">
            <Alert status="error" data-testid="card-number-Errormessage">
              <span>
                {cardError}
                {showFaq && ' For more information '}
                {showFaq
                  && (
                    <Link href={URL.faq} target="_blank" variant="body-sm">
                      see the FAQ
                    </Link>
                  )}
              </span>
            </Alert>
          </div>
        )}
      </div>
      {showAmount && (
        <SelectAmount
          availableAmount={availableAmount}
          reloadableAmount={reloadableAmount}
          isErrorMsg={isDisabled}
          isGraphQlLoading={isGraphQlLoading}
          onNext={handleAddToCart}
          onAmountChange={handleAmountChange}
          isDrawer={isDrawer}
        />
      )}
      <div
        className={isDrawer ? 'sui-w-full' : containerWidthClass}
        data-testid="reload-buttons-wrapper"
      >
        {!showAmount && (
          <div
            className={buttonWrapperClass}
            data-testid="reloadgc-button-wrapper"
          >
            <Button
              fullWidth
              variant="primary"
              onClick={handleShowReload}
              disabled={isDisabled}
            >
              {VERBIAGE.reloadGiftCard}
            </Button>
          </div>
        )}

        {!isDrawer && (
          <>
            <div className={buttonWrapperClass}>
              <Button
                fullWidth
                variant="secondary"
                onClick={handleCheckAnotherBalance}
                disabled={disableBtn}
              >
                {VERBIAGE.checkAnotherBalance}
              </Button>
            </div>
            <div
              className={classNames(buttonWrapperClass, 'sui-text-center', {
                'sui-pointer-events-none': disableBtn
              })}
            >
              <Link href={URL.plp}>{VERBIAGE.backToGiftCards}</Link>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

ReloadForm.propTypes = {
  availableAmount: PropTypes.number,
  cardNumber: PropTypes.string,
  reloadableAmount: PropTypes.number,
  isDrawer: PropTypes.bool,
  cardLast4: PropTypes.string,
  savedPaymentId: PropTypes.string,
  onCancel: PropTypes.func,
  cardError: PropTypes.string,
  isReloadDisabled: PropTypes.bool
};
ReloadForm.defaultProps = {
  availableAmount: 0,
  cardNumber: null,
  reloadableAmount: 0,
  isDrawer: false,
  cardLast4: '',
  savedPaymentId: '',
  cardError: '',
  onCancel: () => { },
  isReloadDisabled: false
};

export default ReloadForm;
